import React, { ReactElement } from 'react';
import type { Voter, EngagementTier } from '../types';

interface VoterCardProps {
  voter: Voter | null;
  onLogVisit?: (voter: Voter) => void;
  compact?: boolean;
  isActive?: boolean;
}

const getTierColor = (tier: EngagementTier | undefined): string => {
  const colors: Record<EngagementTier, string> = {
    super_voter: 'bg-tier-anchor',
    anchor: 'bg-status-success-bg',
    hot: 'bg-tier-hot',
    warm: 'bg-tier-warm',
    cool: 'bg-tier-cool',
  };
  return colors[tier as EngagementTier] || 'bg-tier-cool';
};

const getTierLabel = (tier: EngagementTier | undefined): string => {
  const labels: Record<EngagementTier, string> = {
    super_voter: 'Super Voter',
    anchor: 'Anchor',
    hot: 'Hot',
    warm: 'Warm',
    cool: 'Cool',
  };
  return labels[tier as EngagementTier] || 'Unclassified';
};

const getScoreBadgeColor = (score: number): string => {
  if (score >= 75) return 'bg-tier-anchor';
  if (score >= 60) return 'bg-tier-hot';
  if (score >= 40) return 'bg-tier-warm';
  return 'bg-tier-cool';
};

const getPartyColor = (party: string | null | undefined): string => {
  if (party === 'R') return 'text-status-danger font-bold';
  if (party === 'D') return 'text-status-info font-bold';
  return 'text-fg-muted';
};

export const VoterCard: React.FC<VoterCardProps> = ({
  voter,
  onLogVisit,
  compact = false,
  isActive = false,
}): ReactElement | null => {
  if (!voter) {
    return (
      <div className="p-4 text-center text-fg-muted">No voter selected</div>
    );
  }

  const cardClass = `
    ${isActive ? 'ring-2 ring-accent' : ''}
    ${compact ? 'p-3' : 'p-4'}
    bg-bg-raised border border-border-base rounded-lg hover:border-border-subtle transition-colors
  `;

  return (
    <div className={cardClass}>
      {/* Header with score ring */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-fg-primary">
            {voter.first_name} {voter.last_name}
          </h3>
          <p className="text-sm text-fg-muted">{voter.address}</p>
          <p className="text-xs text-fg-muted">
            {voter.city}, {voter.zip_code}
          </p>
        </div>

        {/* Score badge */}
        <div className="ml-3 flex flex-col items-center">
          <div
            className={`
              w-16 h-16 rounded-full flex items-center justify-center
              ${getScoreBadgeColor(voter.score)} text-white
              font-bold text-xl shadow-lg
            `}
          >
            {Math.round(voter.score)}
          </div>
          <p className="text-xs text-fg-muted mt-1">Score</p>
        </div>
      </div>

      {/* Engagement tier */}
      {voter.engagement_tier && (
        <div className="mb-3">
          <span
            className={`
              inline-block px-2 py-1 rounded text-white text-xs font-semibold
              ${getTierColor(voter.engagement_tier)}
            `}
          >
            {getTierLabel(voter.engagement_tier)}
          </span>
        </div>
      )}

      {/* Key metrics grid */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
        <div className="bg-bg-elevated rounded p-2">
          <p className="text-fg-muted text-xs">Party</p>
          <p className={`font-bold ${getPartyColor(voter.party)}`}>
            {voter.party || 'N/A'}
          </p>
        </div>

        <div className="bg-bg-elevated rounded p-2">
          <p className="text-fg-muted text-xs">Primary Voter</p>
          <p className="text-fg-primary font-bold">
            {voter.primary_voter ? 'Yes' : 'No'}
          </p>
        </div>

        <div className="bg-bg-elevated rounded p-2">
          <p className="text-fg-muted text-xs">Elections</p>
          <p className="text-fg-primary font-bold">{voter.election_count}</p>
        </div>

        <div className="bg-bg-elevated rounded p-2">
          <p className="text-fg-muted text-xs">Primaries</p>
          <p className="text-fg-primary font-bold">{voter.primary_count}</p>
        </div>
      </div>

      {/* Additional details */}
      {!compact && (
        <div className="bg-bg-elevated rounded p-2 mb-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            {voter.birth_year && (
              <div>
                <p className="text-fg-muted">Age</p>
                <p className="text-fg-primary font-semibold">
                  {new Date().getFullYear() - voter.birth_year}
                </p>
              </div>
            )}
            {voter.precinct_num && (
              <div>
                <p className="text-fg-muted">Precinct</p>
                <p className="text-fg-primary font-semibold">{voter.precinct_num}</p>
              </div>
            )}
            {voter.legislative_district && (
              <div>
                <p className="text-fg-muted">LD</p>
                <p className="text-fg-primary font-semibold">
                  {voter.legislative_district}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log visit button */}
      {onLogVisit && (
        <button
          onClick={() => onLogVisit(voter)}
          className="
            w-full bg-accent hover:bg-accent-hover text-white font-bold
            py-2 px-3 rounded text-sm transition-colors
          "
        >
          Log Visit
        </button>
      )}
    </div>
  );
};

export default VoterCard;
